import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';

import { MOBILE_CONTAINER_WIDTH } from '@mopla/constants';

/**
 * Main container for DTicket-related pages.
 * Handles full-screen layout with proper safe area insets for:
 * - iOS/Android native apps (Capacitor)
 * - iOS Safari/Chrome mobile browsers
 * - Android Chrome mobile browser
 * - Desktop web browsers
 */
export const StyledDTicketPageLayoutContainer = styled(Box)(({ theme }) => ({
	position: 'fixed',
	top: 0,
	left: 0,
	right: 0,
	bottom: 0,
	width: '100%',
	maxWidth: MOBILE_CONTAINER_WIDTH,
	margin: '0 auto',
	height: '100%',
	minHeight: '100dvh', // Use dynamic viewport height for mobile browsers
	backgroundColor: theme.palette.neutral?.main ?? '#FFFFFF',
	display: 'flex',
	flexDirection: 'column',
	overflowY: 'auto',
	overflowX: 'hidden',
	boxShadow: '0px 0px 24px rgba(0, 0, 0, 0.12)',

	// When native keyboard is open on iOS, shrink container so scrollable content
	// remains reachable behind the keyboard (uses CSS var set in appListener.tsx).
	// Only needed on iOS — Android's KeyboardResize.Body already resizes the
	// viewport for position:fixed elements, so applying this there would double-subtract.
	'.platform-ios.keyboard-is-open &': {
		height: 'calc(100% - var(--keyboard-height, 0px))',
		minHeight: 'unset',
	},
}));

/**
 * Header wrapper with top safe area padding.
 * Ensures header content is below notch/status bar on all devices.
 * Used across DTicket pages, payment flows (SEPA, Stripe), and other full-screen layouts.
 */
interface IStyledSafeAreaHeaderProps {
	/** Pin the header to the top while the page scrolls beneath it. */
	sticky?: boolean;
	/** Show a drop shadow — only while the content is scrolled. */
	elevated?: boolean;
}

export const StyledSafeAreaHeader = styled(Box, {
	shouldForwardProp: (prop) => prop !== 'sticky' && prop !== 'elevated',
})<IStyledSafeAreaHeaderProps>(({ theme, sticky, elevated }) => ({
	padding: '16px',
	paddingTop: 'max(16px, env(safe-area-inset-top))',
	flexShrink: 0,
	...(sticky && {
		position: 'sticky',
		top: 0,
		zIndex: 10,
		backgroundColor: theme.palette.neutral?.main ?? '#FFFFFF',
		transition: 'box-shadow 0.2s ease',
	}),
	...(elevated && {
		boxShadow: '0px 3px 10px rgba(0, 0, 0, 0.25)',
	}),
}));

interface IStyledDTicketPageLayoutContentProps {
	scrollable?: boolean;
}

/**
 * Content wrapper that can be either scrollable or fixed.
 * - scrollable=true: Content scrolls within this container (e.g., UserDTicket)
 * - scrollable=false: Content is fixed, internal elements handle their own scroll (e.g., DownloadInvoices)
 */
export const StyledDTicketPageLayoutContent = styled(Box, {
	shouldForwardProp: (prop) => prop !== 'scrollable',
})<IStyledDTicketPageLayoutContentProps>(({ scrollable = true }) => ({
	flex: '0 0 auto',
	marginTop: '16px',
	display: 'flex',
	flexDirection: 'column',
	position: 'relative',
	padding: '0 16px',
	minHeight: 0, // Important for flex children to allow shrinking
	...(scrollable
		? {
				overflowY: 'auto',
				overflowX: 'hidden',
				WebkitOverflowScrolling: 'touch',
				paddingBottom: 'max(24px, env(safe-area-inset-bottom))',
		  }
		: {
				overflow: 'hidden',
		  }),
}));

/**
 * Footer wrapper with bottom safe area padding.
 * Used for fixed bottom elements like CTAs or action buttons.
 * Positioned fixed at bottom, centered with max-width matching the layout.
 */
export const StyledDTicketPageLayoutFooter = styled(Box)(({ theme }) => ({
	position: 'fixed',
	bottom: 0,
	left: '50%',
	transform: 'translateX(-50%)',
	width: '100%',
	maxWidth: MOBILE_CONTAINER_WIDTH,
	boxSizing: 'border-box',
	padding: '16px',
	paddingBottom: 'max(24px, env(safe-area-inset-bottom))',
	backgroundColor: theme.palette.neutral?.main ?? '#FFFFFF',
	zIndex: 10,
}));
